import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Terminal, AlertCircle, User, Mail, Lock } from "lucide-react";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Signup() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const data = await register(email, password, name);
      // AuthGate / ProtectedRoute will bounce the (still-unverified) user
      // to /verify-email regardless — explicitly navigate ourselves so we
      // can hand the gate the reclaimed flag for the correct copy.
      const reclaimed = data?.verification_resent ? "?reclaimed=1" : "";
      nav(`/verify-email${reclaimed}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-zinc-900 bg-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-50" />
        <Link to="/" className="relative flex items-center gap-2 z-10">
          <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black">
            <Terminal size={16} strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg">
            Hypex<span className="text-[#00E5FF]">Labs</span>
          </span>
        </Link>
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00FF41] font-mono mb-4">
            // bootstrap your lab
          </div>
          <h2 className="font-display font-black text-3xl tracking-tight mb-4">
            Hands-on labs beat flashcards.
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Build muscle memory for the CCNA with a real-feeling CLI, instant
            instant feedback, and a readiness score that tells you the truth.
          </p>
        </div>
        <div className="relative z-10 text-xs text-zinc-600 font-mono">
          free forever tier · no credit card
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
            register
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight mb-8">Create account</h1>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">name</label>
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                  placeholder="your name"
                  data-testid="signup-name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                  placeholder="you@engineer.com"
                  data-testid="signup-email"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">password</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                  placeholder="min 6 chars"
                  data-testid="signup-password"
                />
              </div>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 p-2"
                data-testid="signup-error"
              >
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E5FF] text-black font-semibold py-2.5 hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
              data-testid="signup-submit"
            >
              {loading ? "creating…" : "create account"}
            </button>

            <GoogleSignInButton label="sign up with Google" testId="signup-google" />
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            already have one?{" "}
            <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="signup-to-login">
              log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
